import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSociete } from '../context/SocieteContext';
import * as api from '../api';
import {
  LayoutDashboard, Calendar, Wrench, ShoppingCart, Package,
  AlertTriangle, CheckCircle2, ChevronLeft,
  TrendingUp, TrendingDown, RefreshCw, ChevronRight,
  Clock, AlertOctagon, Users, Bell, Check, Trash2, RotateCcw
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList
} from 'recharts';
import { toast } from 'sonner';
import { DiscussionsCard } from '../components/DiscussionsCard';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt  = (n) => n == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
const fmtN = (n) => n == null ? '—' : new Intl.NumberFormat('fr-FR').format(n);

function CardTitle({ children }) {
  return <p className="text-sm font-bold mb-3" style={{ color: 'var(--text)',  }}>{children}</p>;
}

// ─── Tooltips ─────────────────────────────────────────────────────────────────
function TimelineTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded-xl shadow-xl px-3 py-2 text-sm" style={{ borderColor: 'var(--border)', minWidth: 160 }}>
      <p className="font-semibold mb-2" style={{ color: 'var(--text)' }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-0.5">
          <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: p.fill }} />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.name}</span>
          <span className="text-xs font-bold ml-auto" style={{ color: p.fill }}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function StatutTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border rounded-xl shadow-xl px-3 py-2 text-sm" style={{ borderColor: 'var(--border)', minWidth: 140 }}>
      <p className="font-semibold mb-1" style={{ color: d.color }}>{d.label}</p>
      <p className="font-bold" style={{ color: 'var(--text)' }}>{fmt(d.ca_ht)}</p>
      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{fmtN(d.nb)} contrat{d.nb !== 1 ? 's' : ''}</p>
    </div>
  );
}

// ─── Onglet Général ───────────────────────────────────────────────────────────
function StatCard({ title, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl p-5 border" style={{ borderColor: 'var(--border)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon size={20} style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-bold" style={{ color: 'var(--text)',  }}>{value}</p>
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{title}</p>
    </div>
  );
}

function QuickAction({ title, description, icon: Icon, onClick }) {
  return (
    <button onClick={onClick}
      className="flex items-start gap-4 p-4 bg-white rounded-xl border text-left transition-all hover:shadow-md"
      style={{ borderColor: 'var(--border)' }}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--primary-gradient)' }}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="font-semibold text-sm" style={{ color: 'var(--text)',  }}>{title}</p>
        <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{description}</p>
      </div>
    </button>
  );
}

// ─── Card Recouvrement ────────────────────────────────────────────────────────

const RETARD_BUCKETS = [
  { key: 'j1_30',    label: '1–30 j',   color: '#CA8A04' },
  { key: 'j31_60',   label: '31–60 j',  color: '#EA580C' },
  { key: 'j61_90',   label: '61–90 j',  color: '#DC2626' },
  { key: 'j91_plus', label: '> 90 j',   color: '#7F1D1D' },
];

function RecouvrementCard() {
  const navigate = useNavigate();
  const { activeAgences } = useSociete();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.getDashboardRecouvrement()
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [activeAgences]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const totalRetard = data?.total_en_retard ?? 0;
  const hasRetard   = totalRetard > 0.005;
  const maxBucket   = data ? Math.max(...RETARD_BUCKETS.map(b => data[b.key] || 0), 1) : 1;

  return (
    <div
      data-testid="card-recouvrement"
      onClick={() => navigate('/rapports/balances-agees')}
      className="rounded-xl border cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 overflow-hidden h-full"
      style={{ borderColor: hasRetard ? '#FECACA' : 'var(--border)', background: 'var(--card)' }}
    >
      {/* En-tête coloré */}
      <div className="px-5 py-3 flex items-center justify-between"
        style={{ background: hasRetard ? '#FEF2F2' : '#F0FDF4', borderBottom: `1px solid ${hasRetard ? '#FECACA' : '#BBF7D0'}` }}>
        <div className="flex items-center gap-2">
          <AlertOctagon size={16} style={{ color: hasRetard ? '#DC2626' : '#16A34A' }} />
          <span className="text-sm font-bold" style={{ color: hasRetard ? '#DC2626' : '#15803D' }}>
            Retards de paiement clients
          </span>
        </div>
        {/* Même hauteur que les pill-toggles de RemindersCard */}
        <div className="flex items-center p-0.5 rounded-lg" style={{ background: hasRetard ? '#FECACA' : '#BBF7D0' }}>
          <span className="px-3 py-1 rounded-md text-xs font-semibold" style={{ color: hasRetard ? '#DC2626' : '#15803D' }}>
            Balances âgées
          </span>
        </div>
      </div>

      <div className="px-5 py-4">
        {loading ? (
          <div className="flex items-center gap-2 py-2">
            <RefreshCw size={14} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Chargement…</span>
          </div>
        ) : data?.error ? (
          <p className="text-xs" style={{ color: '#DC2626' }}>Odoo indisponible</p>
        ) : (
          <>
            {/* Total en retard + non échu */}
            <div className="flex items-end justify-between mb-4">
              <div>
                <p className="text-xs mb-0.5" style={{ color: 'var(--text-muted)' }}>Total en retard</p>
                <p className="text-2xl font-bold tabular-nums" style={{ color: hasRetard ? '#DC2626' : '#16A34A',  }}>
                  {fmt(totalRetard)}
                </p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 justify-end mb-1">
                  <Users size={12} style={{ color: 'var(--text-muted)' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                    {data?.nb_clients_en_retard ?? 0}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    client{(data?.nb_clients_en_retard ?? 0) !== 1 ? 's' : ''}
                  </span>
                </div>
                {(data?.non_echu ?? 0) > 0 && (
                  <div className="flex items-center gap-1 justify-end">
                    <Clock size={11} style={{ color: '#16A34A' }} />
                    <span className="text-xs" style={{ color: '#16A34A' }}>{fmt(data.non_echu)} non échu</span>
                  </div>
                )}
              </div>
            </div>

            {/* Barres par tranche */}
            {hasRetard ? (
              <div className="space-y-1.5">
                {RETARD_BUCKETS.map(({ key, label, color }) => {
                  const val = data?.[key] ?? 0;
                  const pct = maxBucket > 0 ? Math.round((val / maxBucket) * 100) : 0;
                  if (val < 0.005) return null;
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-xs w-14 flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{label}</span>
                      <div className="flex-1 rounded-full overflow-hidden" style={{ background: '#F3F4F6', height: 6 }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                      </div>
                      <span className="text-xs font-semibold tabular-nums w-20 text-right flex-shrink-0" style={{ color }}>
                        {fmt(val)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center gap-2 py-1">
                <CheckCircle2 size={14} style={{ color: '#16A34A' }} />
                <span className="text-sm font-medium" style={{ color: '#15803D' }}>Aucun retard de paiement</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Card Reminders ──────────────────────────────────────────────────────────
function RemindersCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const PAGE_SIZE = 3;
  const [statut,    setStatut]    = useState('actif');
  const [reminders, setReminders] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [page,      setPage]      = useState(0);

  const TYPE_LABELS = { vente: 'Vente', location: 'Location', reparation: 'Réparation', achat: 'Achat', entreprise: 'Entreprise' };
  const NAV_MAP = {
    vente:      (id) => `/ventes?open_devis=${id}`,
    location:   (id) => `/contrats?contrat_id=${id}`,
    reparation: (id) => `/atelier?open_or=${id}`,
    achat:      (id) => `/achats?open_achat=${id}`,
    entreprise: (id) => `/annuaire?open_ent=${id}`,
  };

  const load = useCallback((s) => {
    setLoading(true);
    api.getReminders({ statut: s })
      .then(r => setReminders(Array.isArray(r.data) ? r.data : []))
      .catch(() => setReminders([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(statut); }, [load, statut]);
  useEffect(() => { setPage(0); }, [statut]);

  const [reponseModal, setReponseModal] = useState(null); // { rem }
  const [reponseTexte, setReponseTexte] = useState('');

  const handleToggleStatut = async (rem) => {
    // Si on marque comme fait, ouvrir le modal de réponse
    if (rem.statut === 'actif') {
      setReponseModal({ rem });
      setReponseTexte('');
      return;
    }
    // Sinon remettre en actif directement
    try {
      await api.patchReminder(rem.reminder_id, { statut: 'actif' });
      load(statut);
      toast.success('Rappel remis en actif');
    } catch { toast.error('Erreur'); }
  };

  const handleConfirmFait = async () => {
    if (!reponseModal) return;
    try {
      await api.patchReminder(reponseModal.rem.reminder_id, {
        statut: 'fait',
        texte_reponse: reponseTexte.trim() || null,
      });
      setReponseModal(null);
      load(statut);
      toast.success('Rappel marqué comme fait');
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (rem) => {
    try {
      await api.deleteReminder(rem.reminder_id);
      load(statut);
      toast.success('Rappel supprimé');
    } catch { toast.error('Erreur'); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const totalPagesRem  = Math.ceil(reminders.length / PAGE_SIZE);
  const displayedReminders = reminders.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <>
    {/* Modal réponse rappel */}
    {reponseModal && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: 'white', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', width: '100%', maxWidth: 480, padding: 24 }}>
          <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Marquer comme fait</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
            {reponseModal.rem.texte}
          </p>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
            Message de réponse (optionnel)
          </label>
          <textarea
            autoFocus
            value={reponseTexte}
            onChange={e => setReponseTexte(e.target.value)}
            placeholder="Décrivez ce que vous avez fait…"
            rows={3}
            style={{ width: '100%', resize: 'vertical', borderRadius: 8, border: '1px solid var(--border)', padding: '8px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setReponseModal(null)}
              style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'white', cursor: 'pointer', fontSize: 13 }}>
              Annuler
            </button>
            <button onClick={handleConfirmFait}
              style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#8B5CF6', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              Valider
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="rounded-xl border overflow-hidden h-full" style={{ borderColor: 'var(--border)', background: 'var(--card)' }} data-testid="card-reminders">
      {/* Header */}
      <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle, #f9fafb)' }}>
        <div className="flex items-center gap-2">
          <Bell size={16} style={{ color: '#8B5CF6' }} />
          <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>Rappels</span>
          {reminders.length > 0 && (
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: '#8B5CF6', color: '#fff' }}>{reminders.length}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Flèches pagination */}
          {totalPagesRem > 1 && (
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="reminder-page-prev"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 5, border: '1px solid var(--border)', background: 'white', color: page === 0 ? 'var(--text-disabled)' : 'var(--text-muted)', cursor: page === 0 ? 'not-allowed' : 'pointer' }}>
                <ChevronLeft size={12} />
              </button>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', padding: '0 4px', whiteSpace: 'nowrap' }}>{page + 1}/{totalPagesRem}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPagesRem - 1, p + 1))}
                disabled={page >= totalPagesRem - 1}
                data-testid="reminder-page-next"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 5, border: '1px solid var(--border)', background: 'white', color: page >= totalPagesRem - 1 ? 'var(--text-disabled)' : 'var(--text-muted)', cursor: page >= totalPagesRem - 1 ? 'not-allowed' : 'pointer' }}>
                <ChevronRight size={12} />
              </button>
            </div>
          )}
          <div className="flex items-center gap-1 p-0.5 rounded-lg" style={{ background: 'var(--bg-muted, #f3f4f6)' }}>
            {['actif', 'fait'].map(s => (
              <button key={s} onClick={() => setStatut(s)} data-testid={`reminder-toggle-${s}`}
                className="px-3 py-1 rounded-md text-xs font-medium transition-all"
                style={{ background: statut === s ? '#8B5CF6' : 'transparent', color: statut === s ? '#fff' : 'var(--text-muted)' }}>
                {s === 'actif' ? 'Actif' : 'Fait'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
        {loading && <div className="px-5 py-4 text-xs text-center" style={{ color: 'var(--text-muted)' }}>Chargement…</div>}
        {!loading && reminders.length === 0 && (
          <div className="px-5 py-4 text-xs text-center" style={{ color: 'var(--text-muted)' }}>
            Aucun rappel {statut === 'actif' ? 'en cours' : 'terminé'}
          </div>
        )}
        {!loading && displayedReminders.map(rem => {
          const overdue = statut === 'actif' && rem.date_echeance < today;
          const navPath = rem.document_id && NAV_MAP[rem.type_document]
            ? NAV_MAP[rem.type_document](rem.document_id)
            : null;
          return (
            <div key={rem.reminder_id} className="px-4 py-2.5" data-testid="reminder-row">

              {/* ── Layout MOBILE (< sm) ─────────────────────────── */}
              <div className="flex flex-col gap-0.5 sm:hidden">
                {/* Ligne 1 : Date · Demandeur · Boutons */}
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {overdue && <AlertTriangle size={10} style={{ color: '#DC2626' }} />}
                    <span className="text-xs font-semibold tabular-nums" style={{ color: overdue ? '#DC2626' : 'var(--text-muted)' }}>
                      {rem.date_echeance?.split('-').reverse().join('/')}
                    </span>
                  </div>
                  <span style={{ color: 'var(--border)', flexShrink: 0 }}>·</span>
                  <span className="text-xs truncate flex-1" style={{ color: 'var(--text-muted)' }}>
                    {rem.created_by_nom || '—'}
                  </span>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {(!rem.assigned_to_id || rem.assigned_to_id === user?.user_id) && (
                      <button onClick={() => handleToggleStatut(rem)}
                        title={statut === 'actif' ? 'Marquer comme fait' : 'Remettre en actif'}
                        className="p-1.5 rounded-lg transition-colors hover:bg-green-50">
                        {statut === 'actif'
                          ? <Check size={13} style={{ color: '#16A34A' }} />
                          : <RotateCcw size={13} style={{ color: '#7C3AED' }} />}
                      </button>
                    )}
                    {rem.created_by_id === user?.user_id && (
                      <button onClick={() => handleDelete(rem)} title="Supprimer"
                        className="p-1.5 rounded-lg transition-colors hover:bg-red-50">
                        <Trash2 size={13} style={{ color: '#DC2626' }} />
                      </button>
                    )}
                  </div>
                </div>
                {/* Ligne 2 : Type document · Numéro */}
                {(rem.type_document || rem.numero_document) && (
                  <div className="flex items-center gap-1.5">
                    {rem.type_document && (
                      <span className="text-xs font-bold flex-shrink-0" style={{ color: '#7C3AED' }}>
                        {TYPE_LABELS[rem.type_document] || rem.type_document}
                      </span>
                    )}
                    {rem.numero_document && (
                      navPath ? (
                        <button onClick={() => navigate(navPath)}
                          className="text-xs font-semibold hover:underline truncate"
                          style={{ color: '#7C3AED' }}>
                          {rem.numero_document}
                        </button>
                      ) : (
                        <span className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>
                          {rem.numero_document}
                        </span>
                      )
                    )}
                  </div>
                )}
                {/* Ligne 3 : Texte */}
                {rem.texte && (
                  <p className="truncate" style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
                    {rem.texte}
                  </p>
                )}
                {rem.texte_reponse && (
                  <p style={{ fontSize: 13, color: '#7C3AED', fontStyle: 'italic', marginTop: 1 }}>
                    ↳ {rem.texte_reponse}
                  </p>
                )}
              </div>

              {/* ── Layout DESKTOP (≥ sm) — inchangé ────────────── */}
              <div className="hidden sm:block">
                <div className="flex items-center gap-2">
                  {/* Date */}
                  <div className="flex items-center gap-1 flex-shrink-0" style={{ width: 114 }}>
                    {overdue && <AlertTriangle size={10} style={{ color: '#DC2626' }} />}
                    <span className="text-xs font-semibold tabular-nums" style={{ color: overdue ? '#DC2626' : 'var(--text-muted)' }}>
                      {rem.date_echeance?.split('-').reverse().join('/')}
                    </span>
                  </div>
                  {/* Demandé par */}
                  <span className="text-xs flex-shrink-0 truncate" style={{ width: 158, color: 'var(--text-muted)' }}>
                    {rem.created_by_nom || '—'}
                  </span>
                  {/* Type + N° doc */}
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    {rem.type_document && (
                      <span className="text-xs font-bold flex-shrink-0"
                        style={{ color: '#7C3AED', minWidth: 91 }}>
                        {TYPE_LABELS[rem.type_document] || rem.type_document}
                      </span>
                    )}
                    {rem.numero_document && (
                      navPath ? (
                        <button onClick={() => navigate(navPath)}
                          className="text-xs font-semibold truncate hover:underline"
                          style={{ color: '#7C3AED' }}>
                          {rem.numero_document}
                        </button>
                      ) : (
                        <span className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>
                          {rem.numero_document}
                        </span>
                      )
                    )}
                  </div>
                  {/* Boutons */}
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {(!rem.assigned_to_id || rem.assigned_to_id === user?.user_id) && (
                      <button onClick={() => handleToggleStatut(rem)}
                        title={statut === 'actif' ? 'Marquer comme fait' : 'Remettre en actif'}
                        className="p-1.5 rounded-lg transition-colors hover:bg-green-50">
                        {statut === 'actif'
                          ? <Check size={13} style={{ color: '#16A34A' }} />
                          : <RotateCcw size={13} style={{ color: '#7C3AED' }} />}
                      </button>
                    )}
                    {rem.created_by_id === user?.user_id && (
                      <button onClick={() => handleDelete(rem)} title="Supprimer"
                        className="p-1.5 rounded-lg transition-colors hover:bg-red-50">
                        <Trash2 size={13} style={{ color: '#DC2626' }} />
                      </button>
                    )}
                  </div>
                </div>
                {/* Ligne description */}
                {rem.texte && (
                  <p className="mt-1 truncate pl-1" style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
                    {rem.texte}
                  </p>
                )}
                {rem.texte_reponse && (
                  <p className="mt-0.5 pl-1" style={{ fontSize: 13, color: '#7C3AED', fontStyle: 'italic' }}>
                    ↳ {rem.texte_reponse}
                  </p>
                )}
              </div>

            </div>
          );
        })}
      </div>
    </div>
    </>
  );
}

function TabGeneral() {
  const navigate = useNavigate();
  const { activeAgences } = useSociete();
  const [stats, setStats] = useState({ total: 0, loue: 0, en_reparation: 0 });

  const load = useCallback(() => {
    api.getMaterielsStats()
      .then(res => setStats({ total: res.data.total, loue: res.data.par_statut.loue, en_reparation: res.data.par_statut.en_reparation }))
      .catch(() => {});
  }, [activeAgences]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        Bienvenue sur Bedee — Gérez votre activité de location et réparation de matériel
      </p>
      {/* Row 1 : Recouvrement + Rappels côte à côte */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecouvrementCard />
        <RemindersCard />
      </div>
      {/* Row 2 : Discussions pleine largeur */}
      <div>
        <DiscussionsCard />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Matériels en parc"      value={stats.total}          icon={Package}      color="var(--primary-solid)" />
        <StatCard title="Locations actives"       value={stats.loue}           icon={Calendar}     color="var(--primary-light)" />
        <StatCard title="Réparations en cours"    value={stats.en_reparation}  icon={Wrench}       color="#D97706" />
        <StatCard title="Devis en attente"        value="0"                    icon={ShoppingCart} color="#7C3AED" />
      </div>
      <div>
        <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text)',  }}>Actions rapides</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <QuickAction title="Ajouter un matériel"  description="Enregistrer un nouveau matériel dans votre parc"  icon={Package}  onClick={() => navigate('/materiels')} />
          <QuickAction title="Créer un contrat"     description="Établir un nouveau contrat de location"           icon={Calendar} onClick={() => navigate('/contrats')} />
          <QuickAction title="Ouvrir un OR"         description="Créer un ordre de réparation"                     icon={Wrench}   onClick={() => navigate('/atelier')} />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border" style={{ borderColor: 'var(--border)' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text)',  }}>Matériels récemment ajoutés</h3>
          <div className="text-center py-8">
            <Package size={32} className="mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun matériel pour le moment</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border" style={{ borderColor: 'var(--border)' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text)',  }}>Prochaines échéances</h3>
          <div className="text-center py-8">
            <Calendar size={32} className="mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucune échéance à venir</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Onglet Location ──────────────────────────────────────────────────────────
function TabLocation() {
  const navigate = useNavigate();
  const { activeAgences } = useSociete();
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const todayRef = new Date();
  const [mois,  setMois]  = useState(todayRef.getMonth() + 1);
  const [annee, setAnnee] = useState(todayRef.getFullYear());

  const MOIS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

  const naviguer = (delta) => {
    let nm = mois + delta, na = annee;
    if (nm < 1)  { nm = 12; na = annee - 1; }
    if (nm > 12) { nm = 1;  na = annee + 1; }
    setMois(nm); setAnnee(na);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.getDashboardLocation({ mois, annee }); setData(r.data); setLastRefresh(new Date()); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, [mois, annee, activeAgences]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1,2,3,4].map(i => <div key={i} className="animate-pulse rounded-2xl bg-slate-100 h-72" />)}
      </div>
    );
  }
  if (!data) return null;

  const { funnel, retards, facturation, occupation } = data;
  const totalRetards = (retards?.sorties_en_retard?.length || 0) + (retards?.retours_en_retard?.length || 0);

  // Graphe 1 — données (sans Devis)
  const funnelData = (funnel?.statuts_ca || []).filter(s => s.statut !== 'devis');
  const funnelTotal = funnelData.reduce((sum, s) => sum + (s.ca_ht || 0), 0);

  // Label dans la barre : blanc si la barre est assez large, sinon à droite en gris
  function FunnelBarLabel(props) {
    const { x, y, width, height, value, index } = props;
    if (!value || value === 0) return null;
    const label = fmt(value);
    const fits = width > 80;
    if (fits) {
      return <text x={x + width - 8} y={y + height / 2} textAnchor="end" dominantBaseline="central"
        style={{ fontSize: 11, fontWeight: 700, fill: 'white' }}>{label}</text>;
    }
    return <text x={x + width + 6} y={y + height / 2} textAnchor="start" dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: 600, fill: 'var(--text-muted)' }}>{label}</text>;
  }

  return (
    <div className="space-y-3">

      {/* ── Sélecteur mois / année + refresh ─────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1 bg-white border rounded-xl px-1 py-1" style={{ borderColor: 'var(--border)' }}>
          <button onClick={() => naviguer(-1)}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
            style={{ color: 'var(--text-muted)' }}>
            <ChevronLeft size={15} />
          </button>
          <span className="text-sm font-semibold px-3 min-w-[148px] text-center"
            style={{ color: 'var(--text)',  }}>
            {MOIS_FR[mois - 1]} {annee}
          </span>
          <button onClick={() => naviguer(+1)}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
            style={{ color: 'var(--text-muted)' }}>
            <ChevronRight size={15} />
          </button>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && <p className="text-xs hidden sm:block" style={{ color: 'var(--text-placeholder)' }}>Mis à jour à {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>}
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors hover:bg-slate-50"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {/* ── Grille responsive 2×2 ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Zone 1 — Barres horizontales CA par statut */}
        <div className="bg-white rounded-2xl border p-4" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between mb-3">
            <CardTitle>Analyse des contrats de location</CardTitle>
            <span className="text-base font-bold" style={{ color: 'var(--primary-solid)',  }}>
              {fmt(funnelTotal)}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={funnelData.length * 52 + 16}>
            <BarChart data={funnelData} layout="vertical"
              margin={{ top: 0, right: 90, left: 0, bottom: 0 }}>
              <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="var(--border)" />
              <YAxis dataKey="label" type="category" width={78}
                tick={{ fontSize: 12, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <XAxis type="number" hide />
              <Tooltip content={<StatutTooltip />} cursor={false} />
              <Bar dataKey="ca_ht" isAnimationActive={false} radius={[0, 5, 5, 0]} minPointSize={4}>
                {funnelData.map((entry, i) => (
                  <Cell key={`cell-${i}`} fill={entry.color} />
                ))}
                <LabelList content={<FunnelBarLabel />} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Zone 2 — Retards */}
        <div className="bg-white rounded-2xl border p-4" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CardTitle>Retards</CardTitle>
              {totalRetards > 0 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full -mt-2.5" style={{ background: '#FEE2E2', color: '#DC2626' }}>
                  {totalRetards} alerte{totalRetards > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <button onClick={() => navigate('/location/livraisons')}
              className="flex items-center gap-1 text-xs font-semibold transition-opacity hover:opacity-70"
              style={{ color: 'var(--primary-solid)' }}>
              Voir détail <ChevronRight size={13} />
            </button>
          </div>
          {totalRetards === 0 ? (
            <div className="flex items-center gap-2 py-4">
              <CheckCircle2 size={20} style={{ color: '#16A34A' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: '#15803D' }}>Aucun retard</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Toutes les sorties et retours sont dans les délais.</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  label: 'Sorties en retard',
                  count: (retards?.sorties_en_retard || []).filter(c => c.periode === 'en_retard').length,
                  color: '#DC2626', bg: '#FEF2F2',
                },
                {
                  label: 'Sorties du jour',
                  count: (retards?.sorties_en_retard || []).filter(c => c.periode === 'aujourd_hui').length,
                  color: '#D97706', bg: '#FFFBEB',
                },
                {
                  label: 'Retours en retard',
                  count: (retards?.retours_en_retard || []).filter(c => c.periode === 'en_retard').length,
                  color: '#7C3AED', bg: '#F5F3FF',
                },
                {
                  label: 'Retours du jour',
                  count: (retards?.retours_en_retard || []).filter(c => c.periode === 'aujourd_hui').length,
                  color: '#15803D', bg: '#F0FDF4',
                },
              ].map(({ label, count, color, bg }) => (
                <div key={label} className="rounded-xl px-3 py-3 flex flex-col gap-1" style={{ background: bg }}>
                  <p className="text-xs font-medium" style={{ color }}>{label}</p>
                  <p className="text-2xl font-bold" style={{ color }}>{count}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Zone 3 — Facturation 12 mois */}
        <div className="bg-white rounded-2xl border p-4" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-start justify-between mb-3">
            <CardTitle>Facturation — 12 derniers mois</CardTitle>
            <div className="flex items-center gap-4 text-xs flex-shrink-0">
              <span className="flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#F59E0B' }} />À facturer
              </span>
              <span className="flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#2563EB' }} />Facturé
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={297}>
            <BarChart data={data.facturation_timeline || []} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="25%" barGap={2}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false}
                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k€` : `${v}€`} width={48} />
              <Tooltip content={<TimelineTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
              <Bar dataKey="ca_potentiel" name="À facturer" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="ca_facture"   name="Facturé"   fill="#2563EB" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Zone 4 — Taux d'occupation */}
        <div className="bg-white rounded-2xl border p-4" style={{ borderColor: 'var(--border)' }}>
          <CardTitle>Taux d'occupation par gamme</CardTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { items: occupation?.top8  || [], label: 'Top 8 — Meilleures',      color: '#10B981', icon: TrendingUp },
              { items: occupation?.flop8 || [], label: 'Flop 8 — Sous-utilisées', color: '#F59E0B', icon: TrendingDown },
            ].map(({ items, label, color, icon: Icon }) => (
              <div key={label}>
                <div className="flex items-center gap-1.5 mb-3">
                  <Icon size={12} style={{ color }} />
                  <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</p>
                </div>
                {items.length === 0
                  ? <p className="text-xs" style={{ color: 'var(--text-placeholder)' }}>Aucune donnée</p>
                  : <div className="space-y-2.5">
                      {items.map((g, i) => (
                        <div key={i}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-medium truncate" style={{ color: 'var(--text-secondary)', maxWidth: 160 }}>
                              <span className="font-black mr-1" style={{ color: 'var(--text-placeholder)' }}>#{i + 1}</span>{g.gamme_nom}
                            </span>
                            <span className="font-bold flex-shrink-0 ml-2" style={{ color }}>
                              {g.taux}%<span className="font-normal ml-1" style={{ color: 'var(--text-muted)' }}>({fmtN(g.jours_loues)}j / {fmtN(g.total_mat)} mat.)</span>
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                            <div className="h-full rounded-full" style={{ width: `${Math.min(g.taux, 100)}%`, background: color }} />
                          </div>
                        </div>
                      ))}
                    </div>
                }
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Helpers période ──────────────────────────────────────────────────────────
function getLundi(d) {
  const copy = new Date(d);
  copy.setDate(copy.getDate() - ((copy.getDay() + 6) % 7));
  return copy;
}
function isoDate(d) {
  return d.toISOString().split('T')[0];
}
function labelPeriode(periode, ref) {
  const d = new Date(ref + 'T12:00:00');
  if (periode === 'semaine') {
    const lundi = getLundi(d);
    const dim   = new Date(lundi); dim.setDate(dim.getDate() + 6);
    const opts  = { day: 'numeric', month: 'short' };
    return `${lundi.toLocaleDateString('fr-FR', opts)} – ${dim.toLocaleDateString('fr-FR', { ...opts, year: 'numeric' })}`;
  }
  if (periode === 'mois') return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return d.getFullYear().toString();
}
function naviguerPeriode(periode, ref, delta) {
  const d = new Date(ref + 'T12:00:00');
  if (periode === 'semaine') { d.setDate(d.getDate() + delta * 7); }
  else if (periode === 'mois') { d.setMonth(d.getMonth() + delta); d.setDate(1); }
  else { d.setFullYear(d.getFullYear() + delta); d.setMonth(0); d.setDate(1); }
  return isoDate(d);
}
function refDebut(periode, ref) {
  const d = new Date(ref + 'T12:00:00');
  if (periode === 'semaine') return isoDate(getLundi(d));
  if (periode === 'mois') return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
  return `${d.getFullYear()}-01-01`;
}

// ─── Tooltip personnalisé ─────────────────────────────────────────────────────
function TooltipReparation({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const contrat  = payload.find(p => p.dataKey === 'heures_contrat');
  const pointees = payload.find(p => p.dataKey === 'heures_pointees');
  const ecart    = (pointees?.value ?? 0) - (contrat?.value ?? 0);
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 13, minWidth: 160 }}>
      <p style={{ fontWeight: 700, marginBottom: 6, color: 'var(--text)' }}>{label}</p>
      <p style={{ color: '#6B7280' }}>Contrat : <strong>{contrat?.value?.toFixed(1)}h</strong></p>
      <p style={{ color: '#3B82F6' }}>Pointées : <strong>{pointees?.value?.toFixed(1)}h</strong></p>
      <p style={{ color: ecart >= 0 ? '#16A34A' : '#DC2626', fontWeight: 700, marginTop: 4 }}>
        Écart : {ecart >= 0 ? '+' : ''}{ecart.toFixed(1)}h
      </p>
    </div>
  );
}

// ─── Label écart au-dessus de la barre Pointées ───────────────────────────────
function LabelEcart({ x, y, width, value }) {
  if (value === undefined || value === null) return null;
  const ecart = Number(value);
  const color = ecart >= 0 ? '#16A34A' : '#DC2626';
  const label = (ecart >= 0 ? '+' : '') + ecart.toFixed(1) + 'h';
  return (
    <text x={(x ?? 0) + (width ?? 0) / 2} y={(y ?? 0) - 4}
      textAnchor="middle" fontSize={11} fontWeight={700} fill={color}>
      {label}
    </text>
  );
}

// ─── Label écart sur barre horizontale (Pointées) ────────────────────────────
function LabelEcartH({ x, y, width, height, value }) {
  if (value === undefined || value === null) return null;
  const ecart = Number(value);
  if (Math.abs(ecart) < 0.01) return null;
  const color = ecart >= 0 ? '#16A34A' : '#DC2626';
  const label = (ecart > 0 ? '+' : '') + ecart.toFixed(1) + 'h';
  return (
    <text
      x={(x ?? 0) + (width ?? 0) + 6}
      y={(y ?? 0) + (height ?? 0) / 2 + 4}
      textAnchor="start" fontSize={11} fontWeight={700} fill={color}>
      {label}
    </text>
  );
}

// ─── Tooltip heures facturées ─────────────────────────────────────────────────
function TooltipFacturation({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const pointees  = payload.find(p => p.dataKey === 'heures_pointees');
  const facturees = payload.find(p => p.dataKey === 'heures_facturees');
  const delta     = (facturees?.value ?? 0) - (pointees?.value ?? 0);
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 13, minWidth: 170 }}>
      <p style={{ fontWeight: 700, marginBottom: 6, color: 'var(--text)' }}>{label}</p>
      <p style={{ color: '#94A3B8' }}>Pointées : <strong>{pointees?.value?.toFixed(1)}h</strong></p>
      <p style={{ color: '#1D4ED8' }}>Facturées : <strong>{facturees?.value?.toFixed(1)}h</strong></p>
      <p style={{ color: delta >= 0 ? '#16A34A' : '#D97706', fontWeight: 700, marginTop: 4 }}>
        Delta : {delta >= 0 ? '+' : ''}{delta.toFixed(1)}h
      </p>
    </div>
  );
}

// ─── Onglet Réparation ────────────────────────────────────────────────────────
function TabReparation() {
  const { activeAgences } = useSociete();
  const today = isoDate(new Date());

  const [periode,  setPeriode]  = useState('semaine');
  const [ref,      setRef]      = useState(today);
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);

  const refNorm = refDebut(periode, ref);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { periode, reference: refNorm };
      if (activeAgences?.length) params.agence_ids = activeAgences.map(a => a.agence_id).join(',');
      const r = await api.getDashboardReparation(params);
      setData(r.data);
    } catch { toast.error('Erreur chargement dashboard Réparation'); }
    finally  { setLoading(false); }
  }, [periode, refNorm, activeAgences]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const PERIODES = [
    { key: 'semaine', label: 'Semaine' },
    { key: 'mois',    label: 'Mois'    },
    { key: 'annee',   label: 'Année'   },
  ];

  const techs = data?.techniciens ?? [];

  // Triés décroissant : surplus en haut, plus grand déficit en bas
  const sorted = [...techs].sort((a, b) => b.ecart - a.ecart);
  const chartData = sorted.map(t => ({
    prenom:          t.nom.split(' ')[0],
    nomComplet:      t.nom,
    heures_contrat:  t.heures_contrat,
    heures_pointees: t.heures_pointees,
    ecart:           t.ecart,
  }));

  // KPIs déficit global
  const totalDeficit = techs.reduce((s, t) => s + (t.ecart < 0 ? t.ecart : 0), 0);
  const totalContrat = techs.reduce((s, t) => s + t.heures_contrat, 0);
  const ratioDeficit = totalContrat > 0 ? Math.abs(totalDeficit / totalContrat * 100) : 0;

  const chartHeight = Math.max(180, techs.length * 56);

  return (
    <div>
      {/* ── Barre de filtres ── */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          {PERIODES.map(p => (
            <button key={p.key}
              onClick={() => { setPeriode(p.key); setRef(today); }}
              data-testid={`reparation-periode-${p.key}`}
              style={{
                padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: periode === p.key ? 'var(--primary-solid)' : 'var(--card)',
                color:      periode === p.key ? '#fff' : 'var(--text)',
                border: 'none', borderRight: p.key !== 'annee' ? '1px solid var(--border)' : 'none',
              }}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setRef(r => naviguerPeriode(periode, r, -1))}
            data-testid="reparation-prev"
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', cursor: 'pointer' }}>
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', minWidth: 200, textAlign: 'center' }}>
            {labelPeriode(periode, refNorm)}
          </span>
          <button onClick={() => setRef(r => naviguerPeriode(periode, r, +1))}
            data-testid="reparation-next"
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', cursor: 'pointer' }}>
            <ChevronRight size={16} />
          </button>
        </div>

        {data && !loading && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            {data.nb_jours_ouvres} j. ouvrés · {techs.length} technicien{techs.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* ── Grille 2 colonnes ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' }}>

        {/* ── Colonne 1 : Histogramme ── */}
        <div style={{ background: 'var(--card)', borderRadius: 12, padding: '20px 16px 12px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>

          {/* En-tête : titre + KPI déficit */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Pointage des heures</p>
            {techs.length > 0 && totalDeficit < -0.01 && (
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#DC2626' }}>{totalDeficit.toFixed(1)}h</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>déficit</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#DC2626', marginLeft: 6 }}>({ratioDeficit.toFixed(1)}%)</span>
              </div>
            )}
            {techs.length > 0 && totalDeficit >= -0.01 && (
              <span style={{ fontSize: 12, fontWeight: 600, color: '#16A34A' }}>Tous à jour ✓</span>
            )}
          </div>

          {loading ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Chargement…</div>
          ) : techs.length === 0 ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Aucun technicien atelier sur cette période.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={chartData} layout="vertical"
                  margin={{ top: 4, right: 48, left: 0, bottom: 4 }}
                  barCategoryGap="28%" barGap={3}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <YAxis type="category" dataKey="prenom" width={90}
                    tick={{ fontSize: 12, fill: 'var(--text)' }} axisLine={false} tickLine={false} />
                  <XAxis type="number"
                    tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false}
                    tickFormatter={v => `${v}h`} />
                  <Tooltip content={<TooltipReparation />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />

                  <Bar dataKey="heures_contrat" name="Contrat" fill="#94A3B8" radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="heures_contrat" position="right"
                      formatter={v => v > 0 ? `${v}h` : ''}
                      style={{ fontSize: 10, fill: '#94A3B8' }} />
                  </Bar>

                  <Bar dataKey="heures_pointees" name="Pointées" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.heures_pointees >= entry.heures_contrat ? '#22C55E' : '#F97316'} />
                    ))}
                    <LabelList dataKey="ecart" content={LabelEcartH} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div className="flex items-center gap-4 mt-2 justify-center" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: '#94A3B8', display: 'inline-block' }} />Contrat</span>
                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: '#22C55E', display: 'inline-block' }} />≥ Contrat</span>
                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: '#F97316', display: 'inline-block' }} />&lt; Contrat</span>
              </div>
            </>
          )}
        </div>

        {/* ── Colonne 2 : Heures facturées ── */}
        <div style={{ background: 'var(--card)', borderRadius: 12, padding: '20px 16px 12px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>

          {/* En-tête */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Heures facturées</p>
            {techs.length > 0 && (() => {
              const totalPointees  = techs.reduce((s, t) => s + t.heures_pointees,  0);
              const totalFacturees = techs.reduce((s, t) => s + t.heures_facturees, 0);
              const delta = totalFacturees - totalPointees;
              return (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: delta >= 0 ? '#16A34A' : '#D97706' }}>
                    {delta >= 0 ? '+' : ''}{delta.toFixed(1)}h
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>delta global</span>
                </div>
              );
            })()}
          </div>

          {loading ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Chargement…</div>
          ) : techs.length === 0 ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Aucun technicien atelier sur cette période.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                  data={[...techs].sort((a, b) => b.delta_facturation - a.delta_facturation).map(t => ({
                    prenom:           t.nom.split(' ')[0],
                    nomComplet:       t.nom,
                    heures_pointees:  t.heures_pointees,
                    heures_facturees: t.heures_facturees,
                    delta_facturation: t.delta_facturation,
                  }))}
                  layout="vertical"
                  margin={{ top: 4, right: 48, left: 0, bottom: 4 }}
                  barCategoryGap="28%" barGap={3}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <YAxis type="category" dataKey="prenom" width={90}
                    tick={{ fontSize: 12, fill: 'var(--text)' }} axisLine={false} tickLine={false} />
                  <XAxis type="number"
                    tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false}
                    tickFormatter={v => `${v}h`} />
                  <Tooltip content={<TooltipFacturation />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />

                  <Bar dataKey="heures_pointees" name="Pointées" fill="#94A3B8" radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="heures_pointees" position="right"
                      formatter={v => v > 0 ? `${v.toFixed(1)}h` : ''}
                      style={{ fontSize: 10, fill: '#94A3B8' }} />
                  </Bar>

                  <Bar dataKey="heures_facturees" name="Facturées" radius={[0, 4, 4, 0]}>
                    {[...techs].sort((a, b) => b.delta_facturation - a.delta_facturation).map((entry, i) => (
                      <Cell key={i} fill={Math.abs(entry.delta_facturation) < 0.01 ? '#22C55E' : entry.delta_facturation > 0 ? '#3B82F6' : '#F97316'} />
                    ))}
                    <LabelList dataKey="delta_facturation" content={LabelEcartH} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div className="flex items-center gap-4 mt-2 justify-center" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: '#94A3B8', display: 'inline-block' }} />Pointées</span>
                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: '#22C55E', display: 'inline-block' }} />= Pointées</span>
                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: '#3B82F6', display: 'inline-block' }} />&gt; Pointées</span>
                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: '#F97316', display: 'inline-block' }} />&lt; Pointées</span>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

// ─── Page Dashboard principale ────────────────────────────────────────────────
const TABS = [
  { key: 'general',    label: 'Général',    right: 'dashboard_general',    component: TabGeneral    },
  { key: 'location',   label: 'Location',   right: 'dashboard_location',   component: TabLocation   },
  { key: 'reparation', label: 'Réparation', right: 'dashboard_reparation', component: TabReparation },
];

export default function Dashboard() {
  const { user, menuRights } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin_org';
  const isSuperAdmin = user?.role === 'super_admin';
  const rights  = menuRights || [];

  const [ghostCount, setGhostCount] = useState(null);
  useEffect(() => {
    if (!isSuperAdmin) return;
    api.getGhostStockCount()
      .then(r => setGhostCount(r.data.count))
      .catch(() => {});
  }, [isSuperAdmin]);

  const SQL_GHOSTS = `SELECT sa.entry_id, sa.stock_id, s.nom AS depot, sa.article_id, sa.article_nom, sa.quantite_disponible, sa.created_at\nFROM stock_articles sa\nLEFT JOIN stocks s ON s.stock_id = sa.stock_id\nWHERE sa.article_ref IS NULL\nORDER BY sa.created_at DESC;`;

  const visibleTabs = TABS.filter(t => isAdmin || rights.includes(t.right));

  const [activeTab, setActiveTab] = useState(() =>
    visibleTabs[0]?.key || 'general'
  );

  // Si l'onglet actif n'est plus visible (changement de droits), revenir au premier
  const currentTab = visibleTabs.find(t => t.key === activeTab)
    ? activeTab
    : (visibleTabs[0]?.key || 'general');

  const ActiveComponent = TABS.find(t => t.key === currentTab)?.component;

  if (visibleTabs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-sm" style={{ color: 'var(--text-muted)' }}>
        Aucun tableau de bord accessible avec vos droits actuels.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold mb-3"
          style={{ color: 'var(--primary-solid)' }}>
          Tableau de bord
        </h1>

        {/* Bannière ghost stocks — super_admin uniquement */}
        {isSuperAdmin && ghostCount !== null && ghostCount > 0 && (
          <div data-testid="ghost-stock-banner"
            className="mb-4 rounded-lg p-3 text-sm"
            style={{ background: '#FEF3C7', border: '1px solid #F59E0B', color: '#92400E' }}>
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" style={{ color: '#D97706' }} />
              <div>
                <span className="font-semibold">{ghostCount} entrée{ghostCount > 1 ? 's' : ''} stock_articles avec article_ref IS NULL détectée{ghostCount > 1 ? 's' : ''}.</span>
                <span className="ml-1">Requête Supabase :</span>
                <pre className="mt-1.5 rounded px-2 py-1.5 text-xs font-mono select-all whitespace-pre-wrap break-all"
                  style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#78350F' }}>
                  {SQL_GHOSTS}
                </pre>
              </div>
            </div>
          </div>
        )}
        <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
          {visibleTabs.map(t => (
            <button key={t.key}
              onClick={() => setActiveTab(t.key)}
              data-testid={`tab-dashboard-${t.key}`}
              className="px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px"
              style={{
                borderColor: currentTab === t.key ? 'var(--primary-solid)' : 'transparent',
                color: currentTab === t.key ? 'var(--primary-solid)' : 'var(--text-muted)',
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {ActiveComponent && <ActiveComponent />}
    </div>
  );
}
